import {Box, Button, Hero} from '@primer/react-brand/esm'
import {hpcAttrs} from '@github-ui/hpc'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {clsx} from 'clsx'
import {useEffect, useState} from 'react'
import {ContentfulAppStoreButtonGroup} from '../ContentfulAppStoreButtonGroup/ContentfulAppStoreButtonGroup'
import {getAnalyticsEvent} from '../../../lib/utils/analytics'
import {getImageSources, MAX_CONTENT_WIDTH} from '../../../lib/utils/images'
import styles from './ContentfulHero.module.css'
import {ContentfulAnimatedVideo} from '../ContentfulAnimatedVideo/ContentfulAnimatedVideo'
import {IframeVideo} from '../../IframeVideo/IframeVideo'
import type {ContentfulHeroProps} from './contentful-hero.types'
import {renderSingleLineRichText} from '../shared/RichTextFieldRenderer'
import {useNinetailedTrack} from '../../ninetailed/NinetailedTrackContext'

interface ContentfulHeroLayoutProps extends ContentfulHeroProps {
  /**
   * Whether the parent `ContentfulHero` resolved any Ninetailed experiences for
   * this hero. Controls whether CTA `onClick` track handlers are attached.
   * Provided by `ContentfulHero`; defaults to `false` for direct callers.
   */
  hasExperiences?: boolean
}

/**
 * Hero layout component used by both baseline and Ninetailed experiences.
 * Handles all the visual rendering logic for ContentfulHero.
 */
export function ContentfulHeroLayout({
  component,
  paddingBlockEnd,
  analyticsContext,
  trailingComponentOverride,
  hasExperiences = false,
  ...props
}: ContentfulHeroLayoutProps) {
  const enableCodeBlocks = isFeatureEnabled('contentful_primer_code_blocks')
  const track = useNinetailedTrack()
  const {
    align,
    callToActionPrimary,
    callToActionSecondary,
    description,
    descriptionRich,
    descriptionVariant,
    heading,
    headingSize,
    image,
    imagePosition,
    label,
    increaseLabelContrast,
    trailingComponent,
    videoSrc,
    animatedVideo,
  } = component.fields

  const isHeroImage = image && !videoSrc

  const resolvedTrailingComponent =
    trailingComponentOverride ??
    (trailingComponent !== undefined && trailingComponent.length > 0 ? (
      <ContentfulAppStoreButtonGroup components={trailingComponent} analyticsLabel="hero" analyticsLocation="hero" />
    ) : undefined)

  const hasTrailingComponent = resolvedTrailingComponent !== undefined

  const {heroClassName = 'pb-0'} = props

  // Apply the dark/colorful-background focus override only on /education pages.
  // Read after hydration to avoid SSR/CSR mismatches (window is undefined in Alloy SSR).
  // Ref: https://github.com/github/accessibility-audits/issues/16079#issuecomment-4395753577
  const [isEducationPath, setIsEducationPath] = useState(false)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsEducationPath(window.location.pathname.startsWith('/education'))
  }, [])

  return (
    <Box
      className={clsx(styles.contentfulHeroContainer, isEducationPath && styles.brandFocusOverride)}
      paddingBlockEnd={paddingBlockEnd || {narrow: 96, regular: 128}}
    >
      <Hero
        {...hpcAttrs({enabled: Boolean(props['data-hpc'])})}
        align={align}
        trailingComponent={hasTrailingComponent ? () => resolvedTrailingComponent : undefined}
        className={heroClassName}
      >
        {label &&
          (typeof label === 'string' ? (
            <Hero.Label {...(increaseLabelContrast ? {variant: 'default'} : {})}>{label}</Hero.Label>
          ) : (
            <Hero.Label {...(increaseLabelContrast ? {variant: 'default'} : {})}>{label.fields.text}</Hero.Label>
          ))}

        <Hero.Heading size={headingSize}>{heading}</Hero.Heading>

        {descriptionRich ? (
          <Hero.Description variant={descriptionVariant}>
            {renderSingleLineRichText(descriptionRich, enableCodeBlocks)}
          </Hero.Description>
        ) : description ? (
          <Hero.Description variant={descriptionVariant}>{description}</Hero.Description>
        ) : null}

        {!hasTrailingComponent && (callToActionPrimary || callToActionSecondary) ? (
          <Hero.ButtonGroup>
            {callToActionPrimary ? (
              <Button
                as="a"
                href={callToActionPrimary.fields.href}
                data-ref={`hero-primary-action-${callToActionPrimary.sys.id}`}
                data-vendor-pixel-event=""
                {...getAnalyticsEvent(
                  {
                    action: callToActionPrimary.fields.text,
                    tag: 'button',
                    location: 'hero',
                    context: analyticsContext ? `strategy:${analyticsContext}` : 'CTAs',
                  },
                  {context: false},
                )}
                {...(hasExperiences
                  ? ({
                      onClick: () =>
                        track('primary_cta_click', {label: callToActionPrimary.fields.text, location: 'hero'}),
                    } as Record<string, unknown>)
                  : {})}
              >
                {callToActionPrimary.fields.text}
              </Button>
            ) : null}

            {callToActionSecondary ? (
              <Button
                as="a"
                variant="secondary"
                href={callToActionSecondary.fields.href}
                {...getAnalyticsEvent(
                  {
                    action: callToActionSecondary.fields.text,
                    tag: 'button',
                    location: 'hero',
                    context: analyticsContext ? `strategy:${analyticsContext}` : 'CTAs',
                  },
                  {context: false},
                )}
                {...(hasExperiences
                  ? ({
                      onClick: () =>
                        track('secondary_cta_click', {label: callToActionSecondary.fields.text, location: 'hero'}),
                    } as Record<string, unknown>)
                  : {})}
              >
                {callToActionSecondary.fields.text}
              </Button>
            ) : null}
          </Hero.ButtonGroup>
        ) : null}
        {isHeroImage ? (
          <Hero.Image
            as="picture"
            position={imagePosition === 'Inline' ? 'inline-end' : 'block-end'}
            src={`${image.fields.file.url}?fm=webp`}
            sources={getImageSources(image.fields.file.url, {maxWidth: MAX_CONTENT_WIDTH})}
            alt={image.fields.description || ''}
          />
        ) : null}
      </Hero>

      {videoSrc !== undefined && !isHeroImage ? (
        <IframeVideo videoSrc={videoSrc} heading={heading} image={image} className={styles.heroVideo} />
      ) : null}
      {!videoSrc && !image && animatedVideo && (
        <Box paddingBlockStart={80}>
          <ContentfulAnimatedVideo component={animatedVideo} analyticsLocation="hero" />
        </Box>
      )}
    </Box>
  )
}
